import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { CheckCircle2, Sparkles, ArrowRight, MessageCircle, Zap, ShoppingCart } from 'lucide-react';
import { Link } from 'react-router-dom';
import { itemDetails, itemPlanMap, brand } from '../mock';
import { useCart } from '../context/CartContext';
import CheckoutDialog from './CheckoutDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ItemDetailDialog({ itemKey, open, onOpenChange }) {
  const data = itemKey ? itemDetails[itemKey] : null;
  const planId = itemKey ? itemPlanMap[itemKey] : null;
  const [plan, setPlan] = useState(null);
  const [checkout, setCheckout] = useState(false);
  const { add } = useCart();

  useEffect(() => {
    if (!planId) { setPlan(null); return; }
    axios.get(`${API}/plans`)
      .then((r) => {
        const p = (r.data.plans || []).find((x) => x.id === planId);
        setPlan(p || null);
      })
      .catch(() => setPlan(null));
  }, [planId]);

  if (!data) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}><DialogContent /></Dialog>
    );
  }

  const isCustomQuote = plan?.custom_quote;
  const waLink = `https://wa.me/${brand.whatsapp}?text=${encodeURIComponent(
    `Hi, I'm interested in your ${data.title}. Please share more details.`
  )}`;

  const handleBuyNow = () => {
    if (!plan || isCustomQuote) {
      window.open(waLink, '_blank');
      return;
    }
    onOpenChange(false);
    setTimeout(() => setCheckout(true), 200);
  };

  const handleAddToCart = () => {
    if (!plan || isCustomQuote) {
      window.open(waLink, '_blank');
      return;
    }
    add(plan);
    onOpenChange(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl p-0 overflow-hidden rounded-3xl border-0 bg-white">
          {/* Header banner */}
          <div
            className="relative px-8 pt-8 pb-6 text-white"
            style={{
              background: `linear-gradient(135deg, ${data.color} 0%, ${data.color}cc 100%)`,
            }}
          >
            <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-white/15 blur-2xl" />
            <div className="absolute -left-8 -bottom-8 w-32 h-32 rounded-full bg-white/10 blur-2xl" />
            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-[3px] mb-4">
                <Sparkles size={12} /> Service Overview
              </div>
              <DialogHeader className="space-y-2 text-left">
                <DialogTitle className="text-3xl font-black text-white leading-tight">
                  {data.title}
                </DialogTitle>
                <DialogDescription className="text-white/90 text-base font-medium">
                  {data.tagline}
                </DialogDescription>
              </DialogHeader>
            </div>
          </div>

          <div className="px-8 py-6 space-y-6 max-h-[55vh] overflow-y-auto">
            <p className="text-[color:var(--ink)] leading-relaxed">{data.summary}</p>

            <div>
              <div className="text-[11px] font-bold uppercase tracking-widest mb-3" style={{ color: data.color }}>
                What’s included
              </div>
              <ul className="space-y-2">
                {data.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-[color:var(--ink)]">
                    <CheckCircle2 size={18} style={{ color: data.color }} className="mt-0.5 shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>

            {data.outcomes && data.outcomes.length > 0 && (
              <div>
                <div className="text-[11px] font-bold uppercase tracking-widest mb-3 text-[color:var(--ink-soft)]">
                  Typical outcomes
                </div>
                <div className="flex flex-wrap gap-2">
                  {data.outcomes.map((o) => (
                    <span
                      key={o}
                      className="px-3 py-1.5 rounded-full text-xs font-semibold border"
                      style={{
                        background: `${data.color}12`,
                        color: data.color,
                        borderColor: `${data.color}40`,
                      }}
                    >
                      {o}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {plan && !isCustomQuote && (
              <div className="rounded-2xl p-4 border" style={{ background: `${data.color}0d`, borderColor: `${data.color}33` }}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-widest" style={{ color: data.color }}>
                      {plan.duration}
                    </div>
                    <div className="text-2xl font-black text-[color:var(--ink)] mt-1">
                      ₹{plan.base_amount.toLocaleString('en-IN')}
                      <span className="text-xs font-semibold text-[color:var(--ink-soft)] ml-2">
                        {plan.gst_included ? '(incl. GST)' : '+ GST'}
                      </span>
                    </div>
                  </div>
                  <Sparkles size={20} style={{ color: data.color }} />
                </div>
              </div>
            )}
          </div>

          <div className="px-8 py-5 bg-neutral-50 border-t border-neutral-100 space-y-2">
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                onClick={handleBuyNow}
                className="flex-1 inline-flex items-center justify-center gap-2 font-semibold text-white rounded-full py-3 text-sm transition hover:opacity-90"
                style={{ background: data.color }}
              >
                {isCustomQuote || !plan ? (
                  <><MessageCircle size={16} /> Get Custom Quote</>
                ) : (
                  <><Zap size={16} /> Buy Now</>
                )}
              </button>
              <button
                onClick={handleAddToCart}
                className="flex-1 inline-flex items-center justify-center gap-2 font-semibold rounded-full py-3 text-sm border-2 hover:bg-white transition"
                style={{ borderColor: `${data.color}80`, color: data.color }}
              >
                {isCustomQuote || !plan ? (
                  <><MessageCircle size={16} /> Ask on WhatsApp</>
                ) : (
                  <><ShoppingCart size={16} /> Add to Cart</>
                )}
              </button>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Link
                to="/contact"
                onClick={() => onOpenChange(false)}
                className="flex-1 inline-flex items-center justify-center gap-2 font-semibold rounded-full py-2.5 text-xs text-[color:var(--ink-soft)] hover:text-[color:var(--ink)] transition"
              >
                Contact Sales <ArrowRight size={14} />
              </Link>
              <a
                href={waLink}
                target="_blank"
                rel="noreferrer"
                className="flex-1 inline-flex items-center justify-center gap-2 font-semibold rounded-full py-2.5 text-xs text-[#25D366] hover:opacity-80 transition"
              >
                <MessageCircle size={14} /> Chat on WhatsApp
              </a>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <CheckoutDialog
        plan={plan}
        open={checkout}
        onOpenChange={(v) => !v && setCheckout(false)}
      />
    </>
  );
}
